import React from 'react';
import { fetchContact } from '../../../../utils/fetchContact';
import { useEmailContext } from '../../../../contexts/EmailContext';
import { Loader, Box, Typography } from '@coolblue-development/freddy';
import CloseEmailButton from './CloseEmailButton';
import { Label } from '../../Label';

type StatusLabel = { color: 'red' | 'orange'; text: string };

const STATUS_CONFIG: Partial<Record<connect.ContactStateType, StatusLabel>> = {
  [connect.ContactStateType.ENDED]: { color: 'orange', text: 'After Contact Work' },
  [connect.ContactStateType.ERROR]: { color: 'red', text: 'Error' },
  [connect.ContactStateType.MISSED]: { color: 'red', text: 'Missed' }
};

const BORDER_COLORS = {
  ENDED: '#FF6600',
  CONNECTED: '#0090E3',
  DEFAULT: '#DDDDDD'
} as const;

interface EmailTabProps {
  contactId: string;
}

const EmailTab: React.FC<EmailTabProps> = ({ contactId }) => {
  const { selectedEmail, setSelectedEmail, closingContactId } = useEmailContext();
  const contact = fetchContact(contactId);
  const unresolvedContactStatus = contact ? contact.getStatus().type : null;
  const contactStatus =
    closingContactId === contactId && unresolvedContactStatus === connect.ContactStateType.ENDED
      ? connect.ContactStateType.CONNECTED
      : unresolvedContactStatus;

  const handleClick = (): void => {
    if (selectedEmail !== contactId) {
      if (contactStatus === connect.ContactStateType.CONNECTED || contactStatus === connect.ContactStateType.ENDED) {
        setSelectedEmail(contactId);
      }
    }
  };

  const getEmailTabStateLabel = (contactStatus: connect.ContactStateType | null): JSX.Element | null => {
    if (!contactStatus || !STATUS_CONFIG[contactStatus]) {
      return null;
    }

    const { color, text } = STATUS_CONFIG[contactStatus];
    return (
      <Label data-testid="email-tile-status" color={color} variant="subtle">
        {text}
      </Label>
    );
  };

  const getEmailTabContent = (contactStatus: connect.ContactStateType | null): JSX.Element | null => {
    switch (contactStatus) {
      case connect.ContactStateType.CONNECTING:
        return <Loader data-testid="email-tile-loading" size="small" />;
      case connect.ContactStateType.ERROR:
      case connect.ContactStateType.MISSED:
        return <CloseEmailButton contactId={contactId} />;
      default:
        return null;
    }
  };

  const getBorderStyle = (): string => {
    if (selectedEmail !== contactId) return BORDER_COLORS.DEFAULT;
    if (contactStatus === connect.ContactStateType.ENDED) return BORDER_COLORS.ENDED;
    if (contactStatus === connect.ContactStateType.CONNECTED) return BORDER_COLORS.CONNECTED;

    return BORDER_COLORS.DEFAULT;
  };

  return (
    <Box
      data-testid="email-tile"
      sx={{
        padding: '8px',
        border: `1px solid ${getBorderStyle()}`,
        marginBottom: '8px',
        borderRadius: '5px',
        position: 'relative'
      }}
      onClick={handleClick}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography data-testid="email-tile-customer" variant="h5">
          Email
        </Typography>
        {getEmailTabStateLabel(contactStatus)}
      </Box>
      <Box sx={{ marginTop: '15px' }}>{getEmailTabContent(contactStatus)}</Box>
    </Box>
  );
};

export default EmailTab;
